import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "reactstrap";
import { Box, Card, Group, SimpleGrid, Stack, Table, Text, Title } from "@mantine/core";
import moment from "moment";
import { bindActionCreators } from "redux";

import { useWindow } from "hooks/UseWindow";

import { ReactComponent as CertificateIcon } from "@/assets/img/Course/registration/success.svg";
import { ReactComponent as BookIcon } from "@/assets/img/Course/upcoming/book.svg";
import { ReactComponent as CalenderIcon } from "@/assets/img/Course/upcoming/calendar.svg";
import { ReactComponent as ClassIcon } from "@/assets/img/Course/upcoming/class.svg";
import { ReactComponent as CourseIcon } from "@/assets/img/Course/upcoming/course_ID_blue.svg";
import { ReactComponent as TimeIcon } from "@/assets/img/Course/upcoming/date_blue.svg";
import { ReactComponent as DownloadIcon } from "@/assets/img/Course/upcoming/download.svg";
import { ReactComponent as DurationIcon } from "@/assets/img/Course/upcoming/duration-green.svg";
import { ReactComponent as LocationIcon } from "@/assets/img/Course/upcoming/location_blue.svg";
import { ReactComponent as MapMarkerIcon } from "@/assets/img/Course/upcoming/map-marker.svg";
import { ReactComponent as MealIcon } from "@/assets/img/Course/upcoming/meal.svg";
import { ReactComponent as PhoneIcon } from "@/assets/img/Course/upcoming/phone_blue.svg";
import { ReactComponent as SupportIcon } from "@/assets/img/Course/upcoming/support.svg";
import { ReactComponent as TeachIcon } from "@/assets/img/Course/upcoming/teach.svg";
import { ReactComponent as UserIcon } from "@/assets/img/Course/upcoming/user_blue.svg";

import { ThemedButton } from "../ThemedButton";

const VERSION = process.env.REACT_APP_APP_VERSION;

function CourseInfo({
	course_title,
	course_logos,
	trainer_name,
	trainer_image_url,
	course_duration,
	course_categories,
	course_ref,
	courserun_modeoftraining,
	venue_address,
	training_support_name,
	training_support_mobile,
	courserun_course_from,
	courserun_course_to,
	csessions,
	course_description,
	course_reschedule_status,
	course_withdrawal_status,
	pending_assessment_date,
	course_attendance_status,
	tobe_rescheduled,
	claim_id,
	sfc_claim_id,
	claim_status,
	course_banner_image,
	course_short_description,
	registered_learners,
	message,
	status,
	course,
	handlePopup,
	handleSFCClick,
	showButtons,
}) {
	const { width } = useWindow();
	const [certModalChange, setCertModalChange] = useState(false);

	const getStatusTag = (
		course_reschedule_status,
		tobe_rescheduled,
		course_withdrawal_status,
		pending_assessment_date,
	) => {
		if (course_withdrawal_status == "PENDING") {
			return (
				<span className="p-2 ml-2 course-sticker pending-withdrawal">
					{"Withdrawal Processing"}
				</span>
			);
		}
		if (course_withdrawal_status == "REJECTED") {
			return <span className="p-2 ml-2 course-sticker rejected">{"Withdrawal Rejected"}</span>;
		}
		if (tobe_rescheduled && course_reschedule_status !== "Submitted") {
			return (
				<span className="p-2 ml-2 course-sticker pending-reschedule">{"Reschedule Required"}</span>
			);
		}
		if (course_reschedule_status == "Submitted") {
			return (
				<span className="p-2 ml-2 course-sticker pending-reschedule">
					{"Reschedule Processing"}
				</span>
			);
		}
		if (
			course_reschedule_status == "Pending SFC Cancellation" ||
			course_reschedule_status == "Pending SFC Resubmission" ||
			course_reschedule_status == "Pending SFC Approval"
		) {
			return (
				<span className="p-2 ml-2 course-sticker pending-reschedule">
					{course_reschedule_status}
				</span>
			);
		}
		if (course_reschedule_status == "Rejected") {
			return <span className="p-2 ml-2 course-sticker rejected">{"Reschedule Rejected"}</span>;
		}
		if (course_reschedule_status == "Assessment") {
			if (pending_assessment_date) {
				return (
					<>
						<span className="p-2 ml-2 course-sticker pending-assessment">
							{"Pending Assessment"}
						</span>
						<span className="pt-3 d-block course-date">{pending_assessment_date}</span>
					</>
				);
			} else {
				return (
					<>
						<span className="p-2 ml-2 course-sticker pending-assessment">
							{"Pending Assessment"}
						</span>
					</>
				);
			}
		}
	};

	return (
		<Card p={"sm"} bg="transparent">
			<div>
				<SimpleGrid
					cols={{
						base: 1,
						md: 4,
					}}>
					{[
						{
							label: "Course ID",
							children: course_ref ? course_ref : "-",
							icon: <BookIcon width={16} height={16} color="var(--mantine-color-themed-8)" />,
						},

						{
							label: "Location",
							children: venue_address ? venue_address : "-",
							icon: <MapMarkerIcon width={16} height={16} color="var(--mantine-color-themed-8)" />,
						},

						{
							label: "Training Period",
							children:
								courserun_course_from && courserun_course_to ? (
									<>
										<span>{moment(courserun_course_from).format("DD MMM YYYY, ddd")}</span>
										<span> - </span>
										<span style={{ whiteSpace: "nowrap" }}>
											{moment(courserun_course_to).format("DD MMM YYYY, ddd")}
										</span>
									</>
								) : (
									"-"
								),
							icon: <CalenderIcon width={16} height={16} color="var(--mantine-color-themed-8)" />,
						},
						{
							label: "Duration",
							children: course_duration ? `${course_duration} Days of Training` : "-",
							icon: <DurationIcon width={16} height={16} color="var(--mantine-color-themed-8)" />,
						},
						{
							label: "Mode of Training",
							children: courserun_modeoftraining ? courserun_modeoftraining : "-",
							icon: (
								<ClassIcon
									width={13}
									height={16}
									className=""
									color="var(--mantine-color-themed-8)"
								/>
							),
						},
						{
							label: "Support Contact",
							children: (
								<>
									<span>{training_support_name}</span>
									<br />
									<span>{training_support_mobile}</span>
								</>
							),
							icon: <SupportIcon width={16} height={16} color="var(--mantine-color-themed-8)" />,
						},
						{
							label: "Trainer",
							children: trainer_name ? trainer_name : "-",
							icon: <TeachIcon width={16} height={16} color="var(--mantine-color-themed-8)" />,
						},
					].map((item, i) => (
						<Group wrap="nowrap" align="start" key={i}>
							<Box>{item.icon}</Box>
							<Stack gap={0}>
								<Text fw={500}>{item.label}</Text>
								<Text>{item.children}</Text>
							</Stack>
						</Group>
					))}
				</SimpleGrid>

				<SimpleGrid mt={20} cols={{ base: 1, md: 2 }}>
					<Box>
						<Title order={4}>Sessions</Title>
						<Table withRowBorders withColumnBorders withTableBorder>
							<Table.Thead bg="themed.9">
								<Table.Tr>
									<Table.Th c="white">Date</Table.Th>
									<Table.Th c="white">Time</Table.Th>
								</Table.Tr>
							</Table.Thead>
							<Table.Tbody>
								{csessions.map((session, i) => (
									<Table.Tr key={i}>
										<Table.Td>{moment(session.csession_start_date).format("DD MMM YYYY")}</Table.Td>
										<Table.Td>{`${session.csession_start_time} - ${session.csession_end_time}`}</Table.Td>
									</Table.Tr>
								))}
							</Table.Tbody>
						</Table>
					</Box>
					<Box>
						<Title order={4}>Course Information</Title>
						<Card withBorder mt={20}>
							<Text size="sm">{course_description}</Text>
						</Card>
					</Box>
				</SimpleGrid>
			</div>
			<Box>
				{showButtons && message && (
					<div className="form-group">
						<div className="alert alert-danger" role="alert">
							{message}
						</div>
					</div>
				)}

				<Group justify="space-between" px={20} mt={50} mb={50}>
					<LinkButton variant="outline" to="/course" px={50}>
						{"Back"}
					</LinkButton>
					{showButtons ? <></> : null}
					<Group>
						{status == "completed" && (
							<LinkButton
								to="/course/download-resources"
								state={{ course: course }}
								bg="green.1"
								c="black"
								px={50}>
								<DownloadIcon />
								<span>Download Resources</span>
								<i className="fa fa-angle-right" aria-hidden="true"></i>
							</LinkButton>
						)}

						{status == "completed" && course.cert_file_url && (
							<>
								<LinkButton
									to="/certification"
									variant="outline"
									color="green"
									leftSection={<CertificateIcon />}
									px={50}>
									View Certificate
								</LinkButton>
							</>
						)}

						{status == "upcoming" &&
						course.reschedule_status != "Submitted" &&
						course.withdrawal_status != "PENDING" ? (
							/* eslint-disable indent */
							<>
								{!course.cannot_select_food_reason ? (
									<LinkButton
										to="/course/meal-selection"
										state={{
											learner_id: course.learner_id,
											course: course,
											status: status,
										}}
										px={20}>
										<MealIcon />
										<span>Meal Selection</span>
									</LinkButton>
								) : (
									<ThemedButton
										opacity={0.5}
										color="gray.2"
										c="gray.5"
										px={20}
										onClick={(e) => handlePopup(course.cannot_select_food_reason)}>
										<MealIcon />
										<span>Meal Selection</span>
									</ThemedButton>
								)}
							</>
						) : null}

						{
							/* prettier-ignore */
							(status == "upcoming" || status == "to_be_reschedule") && !course.cannot_withdraw_reason ? (
								/* prettier-ignore */
									<LinkButton
										color='themed-black.8'
										to='/course/withdrawal_description'
										state={{
											learner_id: course.learner_id,
											courserun_id: course.courserun_id,
											course_title: course.course.course_title,
											course: course,
											status: status,
										}}
										variant="outline"
										px={20}>
									Withdrawal
									</LinkButton>
								) : (
									<ThemedButton
										color='themed-black.8'
										opacity={0.5}
										onClick={(e) => handlePopup(course.cannot_withdraw_reason)}>
									Withdrawal
									</ThemedButton>
								)
						}

						{
							/* prettier-ignore */
							(status == "upcoming" || status == "to_be_reschedule") && !course.cannot_reschedule_reason ? (
									/* prettier-ignore */
									<LinkButton
										color='themed-black.8'
										variant="outline"
										to='/course/reschedule_description'
										state={{
											learner_id: course.learner_id,
											learner_sfc: course.learner_sfc,
											courserun_id: course.courserun_id,
											course_title: course.course.course_title,
											course: course,
											status: status,
										}}>
									Reschedule
									</LinkButton>
								) : (
									<>
										{course.reschedule_status == "Pending SFC Resubmission" ? (
											// <div
											// 	className='btn-wrap mb-3'
											// 	style={width <= 767 ? { width: "100%" } : { paddingLeft: "10px" }}>
											// 	<Button
											// 		type='button'
											// 		onClick={(e) => handleSFCClick(e)}
											// 		className='btn btn-outline-green h-100'>
											// 		<i className='fa fa-history' aria-hidden='true'></i>
											// 		<span>Resubmit SFC</span>
											// 		<i className='fa fa-angle-right' aria-hidden='true'></i>
											// 	</Button>
											// </div>
											<ThemedButton
												leftSection={<i className='fa fa-history' aria-hidden='true'></i>}
												onClick={(e) => handleSFCClick(e)}
												color='green'
												px={20}
											>
												Resubmit SFC
											</ThemedButton>
										) : (
											<ThemedButton
												color="themed-black.8"
												opacity={0.5}
												onClick={() => handlePopup(course.cannot_reschedule_reason)}
											>
												Reschedule 
											</ThemedButton>
										)}
									</>
								)
						}
					</Group>
				</Group>
			</Box>
		</Card>
	);
}

const LinkButton = ({ to, children, ...rest }) => {
	return (
		<ThemedButton component={Link} to={to} {...rest}>
			{children}
		</ThemedButton>
	);
};

export default CourseInfo;
